import React from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import moment from 'moment';
import { language } from '../appI18n';
import { ConfiguredCalendar } from '../react-components/ConfiguredCalendar';
import { ConfiguredCalendarPopper, ConfiguredCalendarSurface } from './ConfiguredCalendarPopup';
const sameDate = (first, second) => (!first && !second) || !!(first && second && first.isSame(second, 'day'));
/** Attach the configured calendar without replacing an input's name, id or form contract. */
export function attachSingleDateCalendar(input, initial) {
    var _a;
    let options = initial;
    let selected = (_a = initial.selected) === null || _a === void 0 ? void 0 : _a.clone();
    let open = false;
    let restoringFocus = false;
    let destroyed = false;
    const previousHasPopup = input.getAttribute('aria-haspopup');
    const previousExpanded = input.getAttribute('aria-expanded');
    const host = document.createElement('div');
    host.className = 'configured-single-date-popup';
    (input.closest('.MuiDialog-container') || document.body).appendChild(host);
    input.setAttribute('aria-haspopup', 'dialog');
    const format = () => options.format || 'DD.MM.YYYY';
    const parseInput = (value) => {
        const displayFormat = format() === 'L' ? moment.localeData(language).longDateFormat('L') : format();
        // Cart's 0Z.0Z.00ZZ mask deliberately permits a one-digit day/month.
        const formats = [...new Set([displayFormat, displayFormat.replace(/DD/g, 'D'), displayFormat.replace(/MM/g, 'M'),
                displayFormat.replace(/DD/g, 'D').replace(/MM/g, 'M')])];
        if (options.allowShortYear)
            formats.push(...formats.map(value => value.replace(/YYYY/g, 'YY')));
        return moment(value, formats, language, true);
    };
    const valid = (value) => {
        var _a;
        return value.isValid()
            && (!options.minDate || !value.isBefore(options.minDate, 'day'))
            && (!options.maxDate || !value.isAfter(options.maxDate, 'day'))
            && !((_a = options.isDateDisabled) === null || _a === void 0 ? void 0 : _a.call(options, value));
    };
    const writeValue = () => {
        input.value = (selected === null || selected === void 0 ? void 0 : selected.format(format())) || '';
        input.setCustomValidity('');
    };
    const refresh = () => {
        var _a;
        if (destroyed)
            return;
        input.setAttribute('aria-expanded', String(open));
        render(React.createElement(ConfiguredCalendarPopper, { open: open, anchorEl: input, placement: "bottom-start", container: host, restoreFocus: false },
            React.createElement(ConfiguredCalendarSurface, { role: "dialog", "aria-label": input.getAttribute('aria-label') || ((language === null || language === void 0 ? void 0 : language.startsWith('ru')) ? 'Выбор даты' : 'Choose date') },
                options.hint ? React.createElement("div", { className: "configured-calendar-hint", style: { padding: '8px 12px', color: '#000' } }, options.hint) : null,
                React.createElement(ConfiguredCalendar, { value: selected ? [selected] : [], mode: "single", numberOfMonths: (_a = options.numberOfMonths) !== null && _a !== void 0 ? _a : 1, minDate: options.minDate, maxDate: options.maxDate, isDateDisabled: options.isDateDisabled, onMonthChange: month => { var _a; return (_a = options.onMonthChange) === null || _a === void 0 ? void 0 : _a.call(options, month); }, onChange: (values, complete) => {
                        if (!complete || !values[0] || !valid(values[0]))
                            return;
                        commit(values[0]);
                        close(true);
                    } }))), host);
    };
    const commit = (value) => {
        var _a;
        const changed = !sameDate(selected, value);
        selected = value === null || value === void 0 ? void 0 : value.clone();
        writeValue();
        if (changed)
            (_a = options.onChange) === null || _a === void 0 ? void 0 : _a.call(options, selected === null || selected === void 0 ? void 0 : selected.clone());
        refresh();
    };
    const close = (restoreFocus = false) => {
        if (!open)
            return;
        open = false;
        refresh();
        if (restoreFocus && document.activeElement !== input) {
            restoringFocus = true;
            input.focus();
            restoringFocus = false;
        }
    };
    const show = () => {
        var _a;
        if (restoringFocus || input.disabled || options.disabled)
            return;
        // Also observe direct .val('')/browser autofill before reopening a legacy field.
        const value = input.value.trim();
        const parsed = value ? parseInput(value) : undefined;
        if (!value && selected)
            commit(undefined);
        else if (parsed && valid(parsed) && !sameDate(selected, parsed))
            commit(parsed);
        open = true;
        (_a = options.onOpen) === null || _a === void 0 ? void 0 : _a.call(options, (selected || options.minDate || moment()).clone().startOf('month'));
        refresh();
    };
    const acceptInput = () => {
        const value = input.value.trim();
        if (!value) {
            commit(undefined);
            return;
        }
        const parsed = parseInput(value);
        if (valid(parsed))
            commit(parsed);
        else
            input.setCustomValidity(options.invalidMessage || ((language === null || language === void 0 ? void 0 : language.startsWith('ru')) ? 'Укажите корректную дату.' : 'Enter a valid date.'));
    };
    const inputChanged = () => {
        input.setCustomValidity('');
        if (!input.value.trim())
            commit(undefined);
    };
    const acceptBeforeSubmit = (event) => {
        // Native forms can submit on Enter without blurring the date field first.
        if (event.key === 'Enter' && !event.defaultPrevented)
            acceptInput();
    };
    const keydown = (event) => {
        if (!open || event.key !== 'Escape' || event.defaultPrevented)
            return;
        event.preventDefault();
        event.stopPropagation();
        close(true);
    };
    const clickAway = (event) => {
        if (event.target !== input && !host.contains(event.target))
            close();
    };
    input.addEventListener('focus', show);
    input.addEventListener('click', show);
    input.addEventListener('input', inputChanged);
    input.addEventListener('change', acceptInput);
    input.addEventListener('blur', acceptInput);
    input.addEventListener('keydown', acceptBeforeSubmit);
    document.addEventListener('keydown', keydown, true);
    document.addEventListener('mousedown', clickAway, true);
    writeValue();
    refresh();
    return {
        update(next) {
            var _a;
            const changed = !sameDate(options.selected, next.selected);
            options = next;
            if (changed) {
                selected = (_a = next.selected) === null || _a === void 0 ? void 0 : _a.clone();
                writeValue();
            }
            if (options.disabled)
                open = false;
            refresh();
        },
        clear() { selected = undefined; writeValue(); close(); refresh(); },
        destroy() {
            destroyed = true;
            input.removeEventListener('focus', show);
            input.removeEventListener('click', show);
            input.removeEventListener('input', inputChanged);
            input.removeEventListener('change', acceptInput);
            input.removeEventListener('blur', acceptInput);
            input.removeEventListener('keydown', acceptBeforeSubmit);
            document.removeEventListener('keydown', keydown, true);
            document.removeEventListener('mousedown', clickAway, true);
            unmountComponentAtNode(host);
            host.remove();
            if (previousHasPopup === null)
                input.removeAttribute('aria-haspopup');
            else
                input.setAttribute('aria-haspopup', previousHasPopup);
            if (previousExpanded === null)
                input.removeAttribute('aria-expanded');
            else
                input.setAttribute('aria-expanded', previousExpanded);
        },
    };
}
